import { useState, useEffect, useCallback } from 'react'
import { adminApi } from '../services/api'
import {
  ShieldAlert,
  ShieldCheck,
  AlertTriangle,
  Globe,
  Eye,
  Download,
  RefreshCw,
  Play,
  CheckCircle2,
  XCircle,
  Clock,
  TrendingUp,
  MapPin
} from 'lucide-react'

const SEVERITY_STYLE = {
  critical: { bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-800 dark:text-red-300', border: 'border-red-300 dark:border-red-700', label: 'CRITICA' },
  high:     { bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-800 dark:text-orange-300', border: 'border-orange-300 dark:border-orange-700', label: 'ALTA' },
  medium:   { bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-800 dark:text-yellow-300', border: 'border-yellow-300 dark:border-yellow-700', label: 'MEDIA' },
  low:      { bg: 'bg-blue-100 dark:bg-blue-900/30',  text: 'text-blue-800 dark:text-blue-300',  border: 'border-blue-300 dark:border-blue-700',  label: 'BASSA' }
}

const TYPE_LABEL = {
  brute_force: 'Brute Force',
  credential_stuffing: 'Credential Stuffing',
  login_new_country: 'Login da nuovo paese',
  mass_data_export: 'Export dati massivo',
  off_hours_impersonation: 'Impersonation fuori orario',
  recovery_then_export: 'Recovery 2FA + export',
  admin_action_anomaly: 'Anomalia azione admin'
}

const KpiCard = ({ icon: Icon, label, value, sublabel, color = 'gray' }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</p>
        <p className={`mt-1 text-2xl font-bold text-${color}-600 dark:text-${color}-400`}>{value}</p>
        {sublabel && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{sublabel}</p>}
      </div>
      <Icon className={`h-8 w-8 text-${color}-500 opacity-70`} />
    </div>
  </div>
)

const SeverityBadge = ({ severity }) => {
  const s = SEVERITY_STYLE[severity] || SEVERITY_STYLE.low
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${s.bg} ${s.text} border ${s.border}`}>
      {s.label}
    </span>
  )
}

export default function SecurityDashboardPage() {
  const [overview, setOverview] = useState(null)
  const [alerts, setAlerts] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [running, setRunning] = useState(false)
  const [acknowledging, setAcknowledging] = useState(null)
  const [filterSeverity, setFilterSeverity] = useState('')
  const [filterStatus, setFilterStatus] = useState('open')
  const [error, setError] = useState(null)

  const loadData = useCallback(async () => {
    try {
      setError(null)
      const [ovRes, alRes] = await Promise.all([
        adminApi.getSecurityOverview(),
        adminApi.getSecurityAlerts({
          status: filterStatus || undefined,
          severity: filterSeverity || undefined,
          limit: 100
        })
      ])
      setOverview(ovRes.data?.data || null)
      setAlerts(alRes.data?.data?.alerts || [])
    } catch (e) {
      console.error('SecurityDashboard load error', e)
      setError(e.response?.data?.message || 'Errore nel caricamento dei dati di sicurezza')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [filterSeverity, filterStatus])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleRefresh = () => {
    setRefreshing(true)
    loadData()
  }

  const handleRunNow = async () => {
    if (!confirm('Eseguire ora il job di rilevamento anomalie? Analizzerà i log applicativi e potrà generare nuovi alert.')) return
    setRunning(true)
    try {
      await adminApi.runAnomalyDetection()
      await loadData()
    } catch (e) {
      alert('Errore esecuzione job: ' + (e.response?.data?.message || e.message))
    } finally {
      setRunning(false)
    }
  }

  const handleAcknowledge = async (alertId, status) => {
    const note = status === 'false_positive'
      ? prompt('Motivo della classificazione come falso positivo (opzionale):') || ''
      : ''
    setAcknowledging(alertId)
    try {
      await adminApi.acknowledgeSecurityAlert(alertId, { status, note })
      await loadData()
    } catch (e) {
      alert('Errore acknowledge: ' + (e.response?.data?.message || e.message))
    } finally {
      setAcknowledging(null)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <ShieldAlert className="h-7 w-7 text-red-600" />
            Stato Sicurezza
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Indicatori e alert generati dal rilevamento anomalie. Aggiornato ogni ora.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleRunNow}
            disabled={running}
            className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            title="Esegui subito il job di rilevamento"
          >
            <Play className={`h-4 w-4 mr-2 ${running ? 'animate-pulse' : ''}`} />
            {running ? 'In esecuzione...' : 'Esegui ora'}
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Aggiorna
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard
          icon={ShieldAlert}
          label="Alert aperti"
          value={overview?.openAlerts?.total ?? 0}
          sublabel={overview?.openAlerts?.bySeverity?.critical
            ? `${overview.openAlerts.bySeverity.critical} critic${overview.openAlerts.bySeverity.critical === 1 ? 'o' : 'i'}`
            : 'Nessuna criticità'}
          color={overview?.openAlerts?.total > 0 ? 'red' : 'green'}
        />
        <KpiCard
          icon={AlertTriangle}
          label="Login falliti 24h"
          value={overview?.failedLogins?.last24h ?? 0}
          sublabel={`${overview?.failedLogins?.last7d ?? 0} ultimi 7 giorni`}
          color={overview?.failedLogins?.last24h > 50 ? 'red' : 'yellow'}
        />
        <KpiCard
          icon={ShieldCheck}
          label="Login riusciti 24h"
          value={overview?.successfulLogins?.last24h ?? 0}
          sublabel={`${overview?.successfulLogins?.last7d ?? 0} ultimi 7 giorni`}
          color="green"
        />
        <KpiCard
          icon={Eye}
          label="Impersonation 7g"
          value={overview?.impersonations?.last7d ?? 0}
          sublabel="Da superadmin"
          color={overview?.impersonations?.last7d > 5 ? 'orange' : 'gray'}
        />
        <KpiCard
          icon={Download}
          label="Export dati 7g"
          value={overview?.dataExports?.last7d ?? 0}
          sublabel={`${overview?.dataExports?.last30d ?? 0} ultimi 30 giorni`}
          color="blue"
        />
        <KpiCard
          icon={Globe}
          label="Paesi distinti 30g"
          value={overview?.successfulLogins?.distinctCountries?.length ?? 0}
          sublabel="Login riusciti"
          color="purple"
        />
        <KpiCard
          icon={TrendingUp}
          label="Login falliti 30g"
          value={overview?.failedLogins?.last30d ?? 0}
          color="gray"
        />
        <KpiCard
          icon={Clock}
          label="Generato"
          value={overview?.generatedAt ? new Date(overview.generatedAt).toLocaleTimeString('it-IT') : '—'}
          sublabel={overview?.generatedAt ? new Date(overview.generatedAt).toLocaleDateString('it-IT') : ''}
          color="gray"
        />
      </div>

      {/* Timeline 7 giorni */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
          Timeline login ultimi 7 giorni
        </h2>
        <div className="flex gap-1 h-40">
          {(overview?.timeline || []).map(d => {
            const max = Math.max(1, ...(overview?.timeline || []).map(x => x.success + x.failed))
            const successH = (d.success / max) * 100
            const failedH = (d.failed / max) * 100
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1" title={`${d.date}: ${d.success} riusciti, ${d.failed} falliti`}>
                <div className="flex-1 w-full flex flex-col-reverse gap-0.5 min-h-0">
                  <div className="bg-green-500 rounded-sm w-full" style={{ height: `${successH}%`, minHeight: d.success > 0 ? '2px' : '0' }} />
                  <div className="bg-red-500 rounded-sm w-full" style={{ height: `${failedH}%`, minHeight: d.failed > 0 ? '2px' : '0' }} />
                </div>
                <span className="text-[10px] text-gray-500 dark:text-gray-400">
                  {d.success}/{d.failed}
                </span>
                <span className="text-[10px] text-gray-500 dark:text-gray-400">
                  {d.date.slice(8, 10)}/{d.date.slice(5, 7)}
                </span>
              </div>
            )
          })}
        </div>
        <div className="flex gap-4 mt-3 text-xs text-gray-500 dark:text-gray-400">
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded-sm" /> Riusciti</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 rounded-sm" /> Falliti</span>
        </div>
      </div>

      {/* Top IP falliti + Paesi */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3">
            Top IP con login falliti (7g)
          </h2>
          {(overview?.failedLogins?.topIps || []).length === 0 ? (
            <p className="text-sm text-gray-500">Nessun login fallito registrato.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs text-gray-500 uppercase">
                <tr>
                  <th className="text-left py-1">IP</th>
                  <th className="text-right py-1">Tentativi</th>
                  <th className="text-right py-1">Email distinte</th>
                </tr>
              </thead>
              <tbody>
                {overview.failedLogins.topIps.map(row => (
                  <tr key={row.ipAddress} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="py-1 font-mono text-xs">{row.ipAddress}</td>
                    <td className="text-right font-semibold">{row.count}</td>
                    <td className="text-right">{row.distinctEmails}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-3 flex items-center gap-1">
            <MapPin className="h-4 w-4" /> Login riusciti per paese (30g)
          </h2>
          {(overview?.successfulLogins?.distinctCountries || []).length === 0 ? (
            <p className="text-sm text-gray-500">Nessun dato di geolocalizzazione disponibile.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {overview.successfulLogins.distinctCountries.map(c => (
                <span key={c.country} className="inline-flex items-center px-2 py-1 rounded text-xs bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200">
                  <span className="font-mono mr-1">{c.country}</span>
                  <span className="text-gray-500">×{c.count}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Lista alert */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            Alert di sicurezza
          </h2>
          <div className="flex gap-2">
            <select
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-2 py-1"
            >
              <option value="">Tutti gli stati</option>
              <option value="open">Aperti</option>
              <option value="acknowledged">Visionati</option>
              <option value="resolved">Risolti</option>
              <option value="false_positive">Falsi positivi</option>
            </select>
            <select
              value={filterSeverity}
              onChange={e => setFilterSeverity(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-200 rounded px-2 py-1"
            >
              <option value="">Tutte le gravità</option>
              <option value="critical">Critica</option>
              <option value="high">Alta</option>
              <option value="medium">Media</option>
              <option value="low">Bassa</option>
            </select>
          </div>
        </div>

        {alerts.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">
            <ShieldCheck className="h-12 w-12 mx-auto text-green-500 mb-2" />
            Nessun alert {filterStatus === 'open' ? 'aperto' : 'corrispondente ai filtri'}.
          </div>
        ) : (
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {alerts.map(alert => (
              <div key={alert._id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <SeverityBadge severity={alert.severity} />
                      <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                        {TYPE_LABEL[alert.type] || alert.type}
                      </span>
                      <span className="text-xs text-gray-400">
                        {new Date(alert.triggeredAt).toLocaleString('it-IT')}
                      </span>
                      {alert.count > 1 && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200">
                          ×{alert.count} occorrenze
                        </span>
                      )}
                      {alert.status !== 'open' && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                          {alert.status}
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{alert.summary}</p>
                    {alert.userEmail && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        Utente: {alert.userEmail}
                      </p>
                    )}
                    {alert.ipAddress && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                        IP: {alert.ipAddress}
                      </p>
                    )}
                    {alert.acknowledgedNote && (
                      <p className="text-xs italic text-gray-500 dark:text-gray-400 mt-1">
                        Nota: {alert.acknowledgedNote}
                      </p>
                    )}
                  </div>

                  {alert.status === 'open' && (
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={() => handleAcknowledge(alert._id, 'acknowledged')}
                        disabled={acknowledging === alert._id}
                        className="text-xs px-2 py-1 rounded bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 hover:bg-blue-100 disabled:opacity-50 inline-flex items-center gap-1"
                        title="Marca come visionato"
                      >
                        <CheckCircle2 className="h-3 w-3" /> Visionato
                      </button>
                      <button
                        onClick={() => handleAcknowledge(alert._id, 'resolved')}
                        disabled={acknowledging === alert._id}
                        className="text-xs px-2 py-1 rounded bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 hover:bg-green-100 disabled:opacity-50 inline-flex items-center gap-1"
                        title="Marca come risolto"
                      >
                        <CheckCircle2 className="h-3 w-3" /> Risolto
                      </button>
                      <button
                        onClick={() => handleAcknowledge(alert._id, 'false_positive')}
                        disabled={acknowledging === alert._id}
                        className="text-xs px-2 py-1 rounded bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 disabled:opacity-50 inline-flex items-center gap-1"
                        title="Falso positivo"
                      >
                        <XCircle className="h-3 w-3" /> FP
                      </button>
                    </div>
                  )}
                </div>

                {alert.details && Object.keys(alert.details).length > 0 && (
                  <details className="mt-2">
                    <summary className="text-xs text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200">
                      Dettagli tecnici
                    </summary>
                    <pre className="mt-2 text-xs bg-gray-50 dark:bg-gray-900 p-2 rounded overflow-x-auto text-gray-700 dark:text-gray-300">
{JSON.stringify(alert.details, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
